/**
 * CreateSubscriptionDialog Component
 *
 * Dialog for creating recurring client subscriptions.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, DollarSign, User, CalendarDays, Repeat } from "lucide-react";
import {
  useClientSubscriptions,
  SubscriptionInterval,
  SUBSCRIPTION_INTERVAL_CONFIG,
  CreateSubscriptionParams,
} from "@/hooks/use-client-subscriptions";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";

interface Client {
  id: string;
  name: string;
  email: string | null;
}

interface CreateSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  preselectedClientId?: string;
}

export function CreateSubscriptionDialog({
  open,
  onOpenChange,
  onSuccess,
  preselectedClientId,
}: CreateSubscriptionDialogProps) {
  const { organization } = useOrganization();
  const { createSubscription } = useClientSubscriptions();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Form state
  const [clientId, setClientId] = useState(preselectedClientId || "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState<SubscriptionInterval>("month");
  const [intervalCount, setIntervalCount] = useState("1");
  const [sessionsPerPeriod, setSessionsPerPeriod] = useState("");
  const [trialDays, setTrialDays] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  // Load clients on mount
  useEffect(() => {
    if (open) {
      loadClients();
      if (preselectedClientId) {
        setClientId(preselectedClientId);
      }
    }
  }, [open, preselectedClientId]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      if (!organization?.id) return;

      const { data, error } = await supabase
        .from("clients")
        .select("id, name, email")
        .eq("organization_id", organization.id)
        .eq("status", "active")
        .order("name");

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  const resetForm = () => {
    setClientId(preselectedClientId || "");
    setName("");
    setDescription("");
    setAmount("");
    setInterval("month");
    setIntervalCount("1");
    setSessionsPerPeriod("");
    setTrialDays("");
    setSendEmail(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !name || !amount) return;

    setLoading(true);
    try {
      const amountCents = Math.round(parseFloat(amount) * 100);

      const params: CreateSubscriptionParams = {
        client_id: clientId,
        name,
        description: description || undefined,
        amount_cents: amountCents,
        interval,
        interval_count: parseInt(intervalCount) || 1,
        sessions_per_period: sessionsPerPeriod ? parseInt(sessionsPerPeriod) : undefined,
        trial_days: trialDays ? parseInt(trialDays) : undefined,
        send_email: sendEmail,
      };

      await createSubscription(params);
      resetForm();
      onSuccess();
    } catch (error) {
      // Error is handled in the hook
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find((c) => c.id === clientId);

  // Calculate preview text
  const getIntervalPreview = () => {
    const count = parseInt(intervalCount) || 1;
    const config = SUBSCRIPTION_INTERVAL_CONFIG[interval];
    if (count === 1) {
      return config.label;
    }
    return `Every ${count} ${config.plural}`;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetForm();
        onOpenChange(value);
      }}
    >
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Subscription</DialogTitle>
          <DialogDescription>
            Set up recurring billing for your client via Stripe.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Selection */}
          <div className="space-y-2">
            <Label htmlFor="client">Client *</Label>
            <Select value={clientId} onValueChange={setClientId} disabled={!!preselectedClientId}>
              <SelectTrigger>
                <SelectValue placeholder={loadingClients ? "Loading..." : "Select a client"} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {client.name}
                      {client.email && (
                        <span className="text-muted-foreground text-xs">
                          ({client.email})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedClient && !selectedClient.email && (
              <p className="text-xs text-destructive">
                This client must have an email address for subscriptions.
              </p>
            )}
          </div>

          {/* Subscription Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Subscription Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Monthly Training"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                min="0.50"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9"
                required
              />
            </div>
          </div>

          {/* Billing Interval */}
          <div className="space-y-2">
            <Label>Billing Frequency</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="intervalCount" className="text-xs text-muted-foreground">
                  Every
                </Label>
                <Input
                  id="intervalCount"
                  type="number"
                  min="1"
                  max="12"
                  value={intervalCount}
                  onChange={(e) => setIntervalCount(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="interval" className="text-xs text-muted-foreground">
                  Period
                </Label>
                <Select value={interval} onValueChange={(v) => setInterval(v as SubscriptionInterval)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SUBSCRIPTION_INTERVAL_CONFIG).map(([value, config]) => (
                      <SelectItem key={value} value={value}>
                        {config.plural}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Repeat className="h-3 w-3" />
              Billing: {getIntervalPreview()}
              {amount && ` - $${parseFloat(amount).toFixed(2)}`}
            </p>
          </div>

          {/* Sessions Per Period (optional) */}
          <div className="space-y-2">
            <Label htmlFor="sessionsPerPeriod">Sessions Per Period</Label>
            <div className="relative">
              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="sessionsPerPeriod"
                type="number"
                min="1"
                placeholder="Leave empty for unlimited"
                value={sessionsPerPeriod}
                onChange={(e) => setSessionsPerPeriod(e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Optional: Limit sessions included per billing period
            </p>
          </div>

          {/* Trial Period */}
          <div className="space-y-2">
            <Label htmlFor="trialDays">Free Trial (days)</Label>
            <Input
              id="trialDays"
              type="number"
              min="0"
              max="90"
              placeholder="0"
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Client won't be charged during the trial period
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description of what's included"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Send Email Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="sendEmail">Send email to client</Label>
              <p className="text-xs text-muted-foreground">
                {selectedClient?.email
                  ? `Notification will be sent to ${selectedClient.email}`
                  : "Client has no email address"}
              </p>
            </div>
            <Switch
              id="sendEmail"
              checked={sendEmail && !!selectedClient?.email}
              onCheckedChange={setSendEmail}
              disabled={!selectedClient?.email}
            />
          </div>

          {/* Summary Box */}
          {amount && name && (
            <div className="rounded-lg border bg-muted/50 p-4">
              <h4 className="font-medium text-sm mb-2">Subscription Summary</h4>
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">Plan:</span> {name}
                </p>
                <p>
                  <span className="text-muted-foreground">Price:</span> ${parseFloat(amount).toFixed(2)} / {getIntervalPreview().toLowerCase()}
                </p>
                {sessionsPerPeriod && (
                  <p>
                    <span className="text-muted-foreground">Sessions:</span> {sessionsPerPeriod} per period
                  </p>
                )}
                {trialDays && parseInt(trialDays) > 0 && (
                  <p>
                    <span className="text-muted-foreground">Trial:</span> {trialDays} days free
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !clientId || !name || !amount || !selectedClient?.email}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Subscription"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
